import { gql } from '@apollo/client'

import { ATTACHMENT_ATTRIBUTES } from '../attachments/graphQL'
import { DOCUMENT_SIGNATURE_ATTRIBUTES } from '../documentSignatures/graphQL'

const WORKER_ATTRIBUTES = gql`
  fragment workerAttributes on Worker {
    id
    createdAt
    updatedAt
    archivedAt

    trade
    mobile
    address
    dob
    employmentStatus
    emergencyContact
    emergencyContactNumber
    emergencyContactRelationship
    medicalInformation
    allergies
    builderGenerated

    user {
      firstName
      lastName
      email
    }

    inductions {
      id
      createdAt
      type
      status
      templateName
      ... on ProjectInduction {
        project {
          id
          name
        }
        identifier
      }
    }

    subcontractors {
      id
      isBuilder
      company {
        name
      }
    }

    tickets {
      id
      expiry
      number
      obtained
      countryIssued
      stateIssued
      ticketTemplate {
        id
        shortName
        name
      }
      files {
        id
        name
        file {
          name
          url
          contentType
        }
      }
    }

    documentSignatures {
      ...documentSignatureAttributes
    }

    builderDocuments {
      id
      createdAt
      name
      attachment {
        fileName
        url
      }
    }

    accessControls {
      id
      createdAt
      updatedAt

      type
      identifier
      status

      projects {
        id
        name
      }
    }
  }
  ${DOCUMENT_SIGNATURE_ATTRIBUTES}
`

const WORKER_ATTRIBUTES_SUBSET = gql`
  fragment workerAttributesSubset on Worker {
    id
    createdAt
    trade
    mobile
    archivedAt
    mobile

    builderGenerated

    user {
      firstName
      lastName
      email
    }

    subcontractors {
      id
      company {
        name
      }
    }

    tickets {
      id
      latestReviewStatus

      ticketTemplate {
        shortName
      }
    }
  }
`

const WORKER_ATTRIBUTES_COMMS_SUBSET = gql`
  fragment workerAttributesCommsSubset on Worker {
    id
    mobile

    user {
      firstName
      lastName
    }

    subcontractors {
      id
      company {
        name
      }
    }

    mostRecentSignOnDate
  }
`

const WORKER_TICKETS_ATTRIBUTES = gql`
  fragment workerTicketsAttributes on Ticket {
    id
    hidden
    latestReviewStatus
    expiry
    number
    obtained
    countryIssued
    stateIssued
    ticketTemplate {
      shortName
      name
    }
    files {
      id
      name
      file {
        name
        url
        contentType
      }
    }
    builderGenerated
  }
`

export const GET_WORKER_BY_PROJECT = gql`
  query workerByProject_UI($workerId: String!, $projectId: String!) {
    workerByProject(id: $workerId, projectId: $projectId) {
      ...workerAttributes
    }
  }
  ${WORKER_ATTRIBUTES}
`

export const GET_WORKER = gql`
  query worker_UI($workerId: String!) {
    worker(id: $workerId) {
      ...workerAttributes

      projects {
        id
        name
        status
      }

      user {
        profilePicture {
          id
          key
          url
          fileName
        }
      }

      permits {
        id
        name
        overallStatus

        startDate
        endDate

        workers {
          id
          worker {
            id
            user {
              id
              firstName
              lastName
            }
          }
        }
      }
    }
  }
  ${WORKER_ATTRIBUTES}
`

export const GET_WORKER_TO_REASSIGN = gql`
  query workerToReassign_UI($workerId: String!) {
    worker(id: $workerId) {
      id
      user {
        firstName
        lastName
      }
      subcontractors {
        id
        company {
          name
        }
      }
      inductions {
        type
      }
      documentSignatures {
        swmsDoc {
          name
        }
      }
    }
  }
`

export const GET_WORKER_TICKETS = gql`
  query workerTickets_UI($workerId: String!) {
    ticketsByWorker(workerId: $workerId) {
      ...workerTicketsAttributes
    }
  }
  ${WORKER_TICKETS_ATTRIBUTES}
`

export const GET_WORKER_DOCUMENT_SIGNATURES = gql`
  query workerDocumentSignatures_UI($workerId: String!) {
    worker(id: $workerId) {
      id
      documentSignatures {
        ...documentSignatureAttributes
      }
    }
  }
  ${DOCUMENT_SIGNATURE_ATTRIBUTES}
`

export const GET_WORKER_NAME = gql`
  query workerName_UI($workerId: String!) {
    worker(id: $workerId) {
      id
      user {
        firstName
        lastName
      }
    }
  }
`

export const GET_WORKERS_NAME = gql`
  query workersName_UI(
    $ids: [String]
    $pagination: PaginationInput
    $searchString: String
    $filter: WorkersFilterInput
  ) {
    workers(
      ids: $ids
      pagination: $pagination
      searchString: $searchString
      filter: $filter
    ) {
      id
      user {
        firstName
        lastName
        profilePicture {
          url
        }
      }
      subcontractors {
        id
        company {
          name
        }
      }
      trade
    }
    workersAggregation(
      ids: $ids
      pagination: $pagination
      searchString: $searchString
      filter: $filter
    ) {
      totalCount
      totalPages
      offset
      limit
    }
  }
`

export const GET_WORKERS_NAME_WORKER = gql`
  query workersNameWorker_UI($projectId: String!) {
    workers_worker(projectId: $projectId) {
      id
      user {
        firstName
        lastName
      }
      trade
    }
  }
`

export const GET_WORKERS = gql`
  query workers_UI($ids: [String]) {
    workers(ids: $ids) {
      ...workerAttributes
    }
  }
  ${WORKER_ATTRIBUTES}
`

export const GET_WORKER_SUBCONTRACTORS = gql`
  query workerSubcontractors_UI($workerId: String!) {
    worker(id: $workerId) {
      id
      subcontractors {
        id
        isBuilder
        company {
          name
        }
      }
    }
  }
`

export const GET_WORKER_SIGNONS = gql`
  query workerSignOns_UI($workerId: String, $pagination: PaginationInput) {
    workerSignOns(workerId: $workerId, pagination: $pagination) {
      id
      createdAt
      manualSignedOnAt
      manualSignedOffAt
      signedOffAt
      builderGenerated
      integrationGenerated
      integrationType
      contextMetadata {
        createdFrom
        formName
      }

      project {
        id
        createdAt
        name
        timezone
        country
        status
      }
      visitor {
        id
        user {
          firstName
          lastName
        }
      }
      worker {
        id
        user {
          firstName
          lastName
        }
        builderGenerated
      }
    }
    workerSignOnsAggregation(workerId: $workerId, pagination: $pagination) {
      totalCount
      totalPages
      offset
      limit
    }
  }
`

// Subset of Workers data, used for fast list view
export const GET_WORKERS_LIST = gql`
  query workersList_UI(
    $filter: WorkersFilterInput
    $pagination: PaginationInput
    $sort: WorkerSortInput
    $searchString: String
  ) {
    workers(
      filter: $filter
      pagination: $pagination
      sort: $sort
      searchString: $searchString
    ) {
      ...workerAttributesSubset

      user {
        profilePicture {
          url
        }
      }

      projects {
        id
        name
        status
      }
    }
    workersAggregation(
      filter: $filter
      pagination: $pagination
      sort: $sort
      searchString: $searchString
    ) {
      totalCount
      totalPages
      offset
      limit
    }
  }
  ${WORKER_ATTRIBUTES_SUBSET}
`

export const GET_WORKER_PROJECT = gql`
  query workerProject_UI($projectId: String!) {
    workerProject(id: $projectId) {
      id
      name
      status
      timezone
      country
      builder {
        id
        company {
          name
        }
      }
    }
  }
`

export const GET_WORKER_PROJECTS_WORKER = gql`
  query workerProjectsWorker_UI {
    workerProjects {
      id
      name
      status
      timezone
      country
      builder {
        id
        company {
          name
        }
      }
    }
  }
`

export const GET_WORKER_PROJECTS_WITH_SUBBIES_WORKER = gql`
  query workerProjectsWithSubbiesWorker_UI {
    workerProjects {
      id
      name
      status
      timezone
      country
      builder {
        id
        company {
          name
        }
      }
      subcontractors(filter: { includeBuildSubcontractors: true }) {
        id
      }
    }
  }
`

export const GET_WORKERS_COMMS_LIST = gql`
  query workersCommsList_UI($filter: WorkersFilterInput) {
    workers(filter: $filter) {
      ...workerAttributesCommsSubset
    }
  }
  ${WORKER_ATTRIBUTES_COMMS_SUBSET}
`
export const GET_WORKERS_WITH_SIGNONS_COMMS_LIST = gql`
  query workersWithSignon_UI($filter: WorkersWithResourceFilterInput) {
    workersWithSignon(filter: $filter) {
      ...workerAttributesCommsSubset
    }
  }
  ${WORKER_ATTRIBUTES_COMMS_SUBSET}
`
export const GET_WORKERS_WITH_INDUCTIONS_COMMS_LIST = gql`
  query workersWithInduction_UI(
    $filter: WorkersWithResourceFilterInput
    $inductionType: InductionType!
    $inductionStatus: InductionStatus
  ) {
    workersWithInduction(
      filter: $filter
      inductionType: $inductionType
      inductionStatus: $inductionStatus
    ) {
      ...workerAttributesCommsSubset
    }
  }
  ${WORKER_ATTRIBUTES_COMMS_SUBSET}
`

export const GET_WORKERS_CSV = gql`
  query workersListCsv_UI(
    $filter: WorkersFilterInput
    $searchString: String
    $sort: WorkerSortInput
  ) {
    workers(filter: $filter, searchString: $searchString, sort: $sort) {
      trade
      mobile
      address
      dob
      employmentStatus
      emergencyContact
      emergencyContactNumber
      emergencyContactRelationship
      medicalInformation
      allergies
      ...workerAttributesSubset
    }
  }
  ${WORKER_ATTRIBUTES_SUBSET}
`

export const GET_WORKERS_FOR_SEARCH = gql`
  query workersListForSearch_UI(
    $filter: WorkersFilterInput
    $pagination: PaginationInput
    $sort: WorkerSortInput
    $searchString: String
    $ids: [String]
  ) {
    workers(
      ids: $ids
      filter: $filter
      pagination: $pagination
      sort: $sort
      searchString: $searchString
    ) {
      id
      trade
      builderGenerated
      user {
        firstName
        lastName
        email
      }
      subcontractors {
        id
        company {
          name
        }
      }
      mobile
    }
    workersAggregation(
      filter: $filter
      pagination: $pagination
      sort: $sort
      searchString: $searchString
    ) {
      totalCount
      totalPages
      offset
      limit
    }
  }
`

export const GET_WORKERS_BY_PROJECT = gql`
  query workersByProject_UI($projectId: String!) {
    workersByProject(projectId: $projectId) {
      ...workerAttributesSubset
    }
  }
  ${WORKER_ATTRIBUTES_SUBSET}
`

export const GET_STAFF_WORKERS = gql`
  query builderWorkers_UI(
    $filter: BuilderWorkersFilterInput
    $pagination: PaginationInput
    $searchString: String
  ) {
    builderWorkers(
      filter: $filter
      pagination: $pagination
      searchString: $searchString
    ) {
      id
      trade
      user {
        firstName
        lastName
        email
      }
      mobile
    }
    builderWorkersAggregation(
      filter: $filter
      pagination: $pagination
      searchString: $searchString
    ) {
      totalCount
      totalPages
      offset
      limit
    }
  }
`

export const GET_WORKERS_ON_SITE = gql`
  query workersOnSite_UI($projectId: String!) {
    workersOnSite(projectId: $projectId) {
      id
      user {
        firstName
        lastName
      }

      trade
      mobile
    }
  }
`

export const GET_VISITORS_ON_SITE = gql`
  query visitorsOnSite_UI($projectId: String!) {
    visitorsOnSite(projectId: $projectId) {
      id
      mobile
      user {
        id
        firstName
        lastName
      }
    }
  }
`

export const CREATE_WORKER_WITH_USER = gql`
  mutation createWorkerWithUser_UI($input: CreateWorkerUserInput!) {
    createWorkerWithUser(input: $input) {
      id
    }
  }
`

export const UPDATE_WORKER_WITH_USER = gql`
  mutation updateWorkerWithUser_UI($input: UpdateWorkerUserInput!) {
    updateWorkerWithUser(input: $input) {
      id
    }
  }
`
export const BUILDER_WORKER_DOCUMENT_ATTRIBUTES = gql`
  fragment builderWorkerDocumentAttributes on BuilderWorkerDocument {
    id
    createdAt
    updatedAt
    deletedAt

    name

    attachment {
      ...attachmentAttributes
    }
  }
  ${ATTACHMENT_ATTRIBUTES}
`

export const CREATE_BUILDER_WORKER_DOCUMENT = gql`
  mutation createBuilderWorkerDocument_UI(
    $input: CreateBuilderWorkerDocumentInput!
  ) {
    createBuilderWorkerDocument(input: $input) {
      ...builderWorkerDocumentAttributes
    }
  }
  ${BUILDER_WORKER_DOCUMENT_ATTRIBUTES}
`

export const GET_BUILDER_WORKER_DOCUMENT = gql`
  query builderWorkerDocument_UI($id: String!) {
    builderWorkerDocument(id: $id) {
      ...builderWorkerDocumentAttributes
    }
  }
  ${BUILDER_WORKER_DOCUMENT_ATTRIBUTES}
`

export const DELETE_BUILDER_WORKER_DOCUMENT = gql`
  mutation deleteBuilderWorkerDocument_UI($id: String!) {
    deleteBuilderWorkerDocument(id: $id) {
      ...builderWorkerDocumentAttributes
    }
  }
  ${BUILDER_WORKER_DOCUMENT_ATTRIBUTES}
`

export const DELETE_STAFF_SUBCONTRACTOR = gql`
  mutation deleteStaffSubcontractor_UI($input: DeleteStaffSubcontractorInput!) {
    deleteStaffSubcontractor(input: $input) {
      id
    }
  }
`

export const ARCHIVE_WORKER = gql`
  mutation archiveWorker_UI($id: String!) {
    archiveWorker(id: $id) {
      id
    }
  }
`

export const REASSIGN_WORKER_SUBCONTRACTORS = gql`
  mutation reassignWorkerSubcontractors_UI(
    $workerId: String!
    $subcontractorIds: [String!]!
  ) {
    reassignWorkerSubcontractors(
      workerId: $workerId
      subcontractorIds: $subcontractorIds
    ) {
      id
    }
  }
`

export const GET_WORKER_INFO = gql`
  query workerInfo_UI($id: String!) {
    worker(id: $id) {
      id
      trade
      mobile

      user {
        firstName
        lastName
        email
      }
    }
  }
`

export const GET_VISITOR_INFO = gql`
  query visitorInfo_UI($id: String!) {
    visitor(id: $id) {
      id
      mobile

      user {
        firstName
        lastName
        email
      }
    }
  }
`

export const DISCONNECT_WORKER_FROM_BUILDER = gql`
  mutation disconnectWorkerFromBuilder_UI($id: String!) {
    disconnectWorkerFromBuilder(id: $id) {
      id
    }
  }
`
